// History Page Entry Point
import './styles/history.scss';
import type {
  HistoryItem,
  HistoryItemType,
  HistoryListResponse,
  StatsData,
} from './types/history.js';
import {
  API_URL,
  normalizePath,
  loadAuthenticatedMediaSimple as loadAuthenticatedMedia,
} from './utils/ui-helpers.js';

// Auth types are provided globally by src/types/auth.d.ts
// which declares window.initAuth, window.authenticatedFetch, etc.

console.log('MCP History loaded');

// ---------------------------------------------------------------------------
// Thumbnail renderer registry — adding a new type is one entry here
// ---------------------------------------------------------------------------

/** Returns the HTML string for a thumbnail inside the grid card. */
type ThumbnailRenderer = (item: HistoryItem) => string;

/** Badge config per type. `null` means no badge. */
interface BadgeConfig {
  label: string;
  cssClass: string;
}

const BADGE_MAP: Record<HistoryItemType, BadgeConfig | null> = {
  transformation: null,
  inpainting: { label: 'Inpainting', cssClass: 'inpainting' },
  upscale: { label: 'Upscale', cssClass: 'upscale' },
  '3d': { label: '3D', cssClass: 'threed' },
  video: { label: 'Video', cssClass: 'video' },
};

function renderImageThumbnail(item: HistoryItem): string {
  const path = item.resultPath;
  // External URL (e.g., from Replicate CDN)
  if (path?.startsWith('http://') || path?.startsWith('https://')) {
    return `<img src="${path}" class="thumbnail-image" alt="Result" crossorigin="anonymous" />`;
  }
  // API path (e.g., /api/images/{uuid} for upscaled images)
  if (path?.startsWith('/api/')) {
    const resultUrl = `${API_URL}${path}`;
    return `<img data-auth-src="${resultUrl}" class="thumbnail-image auth-image" alt="Result" />`;
  }
  // Local file path – normalize and use authenticated fetch
  const resultPath = normalizePath(path);
  if (!resultPath) {
    return '<p style="color: #999; font-size: 0.8rem; text-align: center;">Niet beschikbaar</p>';
  }
  const resultUrl = `${API_URL}${resultPath}`;
  return `<img data-auth-src="${resultUrl}" class="thumbnail-image auth-image" alt="Result" />`;
}

function render3DThumbnail(item: HistoryItem): string {
  const videoPath = item.combinedVideoPath || item.colorVideoPath;
  if (!videoPath) {
    // Fallback to input image
    const inputPath = normalizePath(item.inputImagePath);
    if (!inputPath) {
      return '<p style="color: #999; font-size: 0.8rem; text-align: center;">Niet beschikbaar</p>';
    }
    const inputUrl = `${API_URL}${inputPath}`;
    return `<img data-auth-src="${inputUrl}" class="thumbnail-image auth-image" alt="3D Input" />`;
  }
  const normalizedPath = normalizePath(videoPath);
  const videoUrl = `${API_URL}${normalizedPath}`;
  return `<canvas data-video-url="${videoUrl}" class="thumbnail-canvas video-thumbnail"></canvas>`;
}

function renderVideoThumbnail(item: HistoryItem): string {
  // For video items, extract a frame from the video.mp4
  const vPath = item.videoPath;
  if (vPath) {
    const normalizedPath = normalizePath(vPath);
    if (normalizedPath) {
      const videoUrl = `${API_URL}${normalizedPath}`;
      return `<canvas data-video-url="${videoUrl}" class="thumbnail-canvas video-thumbnail"></canvas>`;
    }
  }
  // Fallback: use thumbnailPath or inputImagePath
  const fallback = item.thumbnailPath || item.inputImagePath;
  if (fallback) {
    const fallbackPath = normalizePath(fallback);
    if (fallbackPath) {
      const fallbackUrl = `${API_URL}${fallbackPath}`;
      return `<img data-auth-src="${fallbackUrl}" class="thumbnail-image auth-image" alt="Video Input" />`;
    }
  }
  return '<p style="color: #999; font-size: 0.8rem; text-align: center;">Niet beschikbaar</p>';
}

/**
 * Registry mapping item type → thumbnail renderer.
 * To add a new type, add one entry here.
 */
const THUMBNAIL_RENDERERS: Record<HistoryItemType, ThumbnailRenderer> = {
  transformation: renderImageThumbnail,
  inpainting: renderImageThumbnail,
  upscale: renderImageThumbnail,
  '3d': render3DThumbnail,
  video: renderVideoThumbnail,
};

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

void (async function () {
  try {
    await window.initAuth();
    const mainContainer = document.getElementById('mainContainer');
    if (mainContainer) {
      mainContainer.style.display = 'block';
    }
    window.showUserInfo();
    initHistoryPage();
  } catch (err: unknown) {
    console.error('Failed to initialize:', err);
  }
})();

// ---------------------------------------------------------------------------
// Video thumbnail generator (extracts a frame from authenticated video)
// ---------------------------------------------------------------------------

async function generateVideoThumbnail(
  canvas: HTMLCanvasElement,
  videoUrl: string
): Promise<void> {
  try {
    const blobUrl = await loadAuthenticatedMedia(videoUrl);
    if (!blobUrl) {
      console.error('[Thumbnail] Failed to load video');
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Video load failed'));
      video.src = blobUrl;
    });

    const seekTime = Math.min(1, video.duration * 0.1);
    video.currentTime = seekTime;

    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
    });

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    // Cleanup
    video.src = '';
    video.load();
  } catch (error) {
    console.error('[Thumbnail] Failed to generate:', error);
  }
}

// ---------------------------------------------------------------------------
// Page logic
// ---------------------------------------------------------------------------

function initHistoryPage(): void {
  loadStats();
  void loadHistory();

  // Search
  const searchInput = document.getElementById(
    'searchInput'
  ) as HTMLInputElement;
  let searchTimeout: ReturnType<typeof setTimeout>;
  searchInput?.addEventListener('input', (e) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      searchHistory((e.target as HTMLInputElement).value);
    }, 300);
  });

  async function loadStats(): Promise<void> {
    try {
      const response = await window.authenticatedFetch(
        `${API_URL}/api/history/stats`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as StatsData;

      if (!data || !data.stats) {
        throw new Error('Invalid response format');
      }

      const statsGrid = document.getElementById('statsGrid');
      if (statsGrid) {
        statsGrid.innerHTML = `
          <div class="stat-card">
            <div class="stat-value">${data.stats.total || 0}</div>
            <div class="stat-label">Totaal Generaties</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">$${(data.stats.totalCost || 0).toFixed(2)}</div>
            <div class="stat-label">Totale Kosten</div>
          </div>
          ${(data.stats.byProvider || [])
            .map(
              (p) => `
            <div class="stat-card">
              <div class="stat-value">${p.count}</div>
              <div class="stat-label">${p.provider}</div>
            </div>
          `
            )
            .join('')}
        `;
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      const statsGrid = document.getElementById('statsGrid');
      if (statsGrid) {
        statsGrid.innerHTML =
          '<p style="color: #999; text-align: center;">Fout bij laden van statistieken: ' +
          (error as Error).message +
          '</p>';
      }
    }
  }

  async function loadHistory(query = ''): Promise<void> {
    const container = document.getElementById('historyContainer');
    if (!container) return;

    try {
      const url = query
        ? `${API_URL}/api/history/search?q=${encodeURIComponent(query)}`
        : `${API_URL}/api/history?limit=200`;

      const response = await window.authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as HistoryListResponse;

      if (!data || !data.history) {
        throw new Error('Invalid response format');
      }

      const history = data.history;

      if (!Array.isArray(history) || history.length === 0) {
        container.innerHTML =
          '<p style="text-align: center; padding: 2rem; color: #999;">Geen resultaten gevonden.</p>';
        return;
      }

      // Render grid
      container.innerHTML = `
        <div class="thumbnail-grid">
          ${history.map((item) => renderCard(item)).join('')}
        </div>
      `;

      // Load all thumbnails
      await loadThumbnails();
    } catch (error) {
      console.error('Failed to load history:', error);
      container.innerHTML =
        '<p style="text-align: center; padding: 2rem; color: #e74c3c;">Fout bij laden van geschiedenis: ' +
        (error as Error).message +
        '</p>';
    }
  }

  /**
   * Render a single history card.
   * Badge and thumbnail are fully driven by `item.type`.
   */
  function renderCard(item: HistoryItem): string {
    const itemType = item.type || 'transformation';

    // Badge
    let badgeHtml = '';
    const badge = BADGE_MAP[itemType];
    if (badge) {
      badgeHtml = `<div class="thumbnail-badge ${badge.cssClass}">${badge.label}</div>`;
    }

    // FREE badge
    if (item.cost === 0) {
      badgeHtml += '<div class="thumbnail-badge free">FREE</div>';
    }

    // Thumbnail
    const renderer = THUMBNAIL_RENDERERS[itemType] || renderImageThumbnail;
    const thumbnailHtml = renderer(item);

    return `
      <div class="thumbnail-card" onclick="window.goToDetail('${item.id}')" data-item-id="${item.id}">
        ${badgeHtml}
        <div class="thumbnail-placeholder" data-item-id="${item.id}">
          ${thumbnailHtml}
        </div>
      </div>
    `;
  }

  async function loadThumbnails(): Promise<void> {
    // Load images
    const authImages = document.querySelectorAll<HTMLImageElement>(
      'img.auth-image[data-auth-src]'
    );
    console.log('[Thumbnails] Loading', authImages.length, 'images');

    for (const img of authImages) {
      const url = img.getAttribute('data-auth-src');
      if (url) {
        const blobUrl = await loadAuthenticatedMedia(url);
        if (blobUrl) {
          img.src = blobUrl;
        } else {
          img.style.display = 'none';
          if (img.parentElement) {
            img.parentElement.innerHTML +=
              '<p style="color: #999; font-size: 0.8rem; text-align: center;">Niet beschikbaar</p>';
          }
        }
      }
    }

    // Load video thumbnails (both 3D previews and video generation results)
    const videoThumbnails = document.querySelectorAll<HTMLCanvasElement>(
      'canvas.video-thumbnail[data-video-url]'
    );
    console.log('[Thumbnails] Loading', videoThumbnails.length, 'videos');

    for (const canvas of videoThumbnails) {
      const videoUrl = canvas.getAttribute('data-video-url');
      if (videoUrl) {
        await generateVideoThumbnail(canvas, videoUrl);
      }
    }

    console.log('[Thumbnails] All loaded');
  }

  function searchHistory(query: string): void {
    if (query.trim() === '') {
      void loadHistory();
    } else {
      void loadHistory(query);
    }
  }
}

// Make goToDetail globally accessible for onclick handlers
declare global {
  interface Window {
    goToDetail: (itemId: string) => void;
  }
}

window.goToDetail = function (itemId: string): void {
  window.location.href = `/detail.html?id=${itemId}`;
};
